// ChefOrdersDashboard.tsx - النسخة الكاملة والمصححة
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSocket } from '../contexts/SocketContext';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { 
  factoryOrdersAPI, 
  productsAPI, 
  isValidObjectId 
} from '../services/api';
import {
  Package, Clock, CheckCircle, XCircle, User, AlertCircle,
  Eye, Loader2, RefreshCw, BarChart, Play, TrendingUp, AlertTriangle,
  Calendar, ChevronRight, Filter, Search, Info, CheckSquare, Square,
  ChevronLeft, AlertOctagon, PlayCircle, Plus, ShoppingBag, ChefHat,
  ArrowRight, CheckCheck, Ban, Minus, Settings, FileText, ListChecks
} from 'lucide-react';

// ========== الأنواع المحسنة والمتوافقة مع الموديل الخلفي ==========
interface Product {
  _id: string;
  name: string;
  nameEn?: string;
  code: string;
  unit: string;
  unitEn?: string;
  department?: {
    _id: string;
    name: string;
    nameEn?: string;
  };
}

interface ChefOrderItem {
  _id: string;
  orderId: string;
  orderNumber: string;
  itemId: string;
  product: {
    _id: string;
    name: string;
    nameEn?: string;
    code?: string;
    unit: string;
    unitEn?: string;
    department?: {
      _id: string;
      name: string;
      nameEn?: string;
    };
  };
  quantity: number;
  approvedQuantity: number;
  originalQuantity: number;
  status: 'pending_approval' | 'approved' | 'assigned' | 'in_progress' | 'completed' | 'rejected';
  assignedTo?: {
    _id: string;
    name: string;
    nameEn?: string;
    username: string;
  };
  startedAt?: string;
  completedAt?: string;
  rejectReason?: string;
  notes?: string;
  adminNotes?: string;
  requiresApproval: boolean;
  orderCreatedAt: string;
  orderPriority: 'low' | 'medium' | 'high' | 'urgent';
  orderStatus: 'draft' | 'requested' | 'pending' | 'approved' | 'in_production' | 'completed' | 'stocked' | 'cancelled';
}

interface FactoryOrder {
  _id: string;
  orderNumber: string;
  items: Array<{
    _id: string;
    product: Product;
    quantity: number;
    approvedQuantity: number;
    originalQuantity: number;
    status: 'pending_approval' | 'approved' | 'assigned' | 'in_progress' | 'completed' | 'rejected';
    assignedTo?: {
      _id: string;
      name: string;
      nameEn?: string;
    };
    startedAt?: string;
    completedAt?: string;
    rejectReason?: string;
    notes?: string;
    adminNotes?: string;
    requiresApproval: boolean;
  }>;
  status: 'draft' | 'requested' | 'pending' | 'approved' | 'in_production' | 'completed' | 'stocked' | 'cancelled';
  notes?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    _id: string;
    name: string;
    nameEn?: string;
    username: string;
  };
  approvedBy?: {
    _id: string;
    name: string;
    nameEn?: string;
  };
  approvedAt?: string;
  stockedBy?: {
    _id: string;
    name: string;
    nameEn?: string;
  };
  stockedAt?: string;
  inventoryProcessed: boolean;
  stats?: {
    totalItems: number;
    completedItems: number;
    pendingApprovalItems: number;
    inProgressItems: number;
    rejectedItems: number;
    approvedItems: number;
    progressPercentage: number;
  };
}

interface OrderFormItem {
  product: string;
  quantity: number;
  notes?: string;
}

interface OrderFormState {
  items: OrderFormItem[];
  notes: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  orderNumber?: string;
}

interface ActionableItemsResponse {
  data?: {
    pendingApproval?: ChefOrderItem[];
    readyToStart?: ChefOrderItem[];
    inProgress?: ChefOrderItem[];
    completed?: ChefOrderItem[];
    rejected?: ChefOrderItem[];
  };
}

// ========== المكون الرئيسي المحسن ==========
const ChefOrdersDashboard = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { socket, isConnected } = useSocket();
  const isRtl = language === 'ar';

  const [orders, setOrders] = useState<FactoryOrder[]>([]);
  const [actionableItems, setActionableItems] = useState<ChefOrderItem[]>([]);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'assigned' | 'orders'>('assigned');
  
  const [rejectModal, setRejectModal] = useState<{ 
    open: boolean; 
    item: ChefOrderItem | null 
  }>({ 
    open: false, 
    item: null 
  });
  
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [completionNotes, setCompletionNotes] = useState<Record<string, string>>({});
  
  const [createModal, setCreateModal] = useState(false);
  const [newOrder, setNewOrder] = useState<OrderFormState>({
    items: [{ product: '', quantity: 1 }],
    notes: '',
    priority: 'medium',
    orderNumber: `PROD-${Date.now().toString().slice(-8)}`
  });
  
  const [stats, setStats] = useState({
    totalTasks: 0,
    assignedItems: 0,
    inProgressItems: 0,
    completedItems: 0,
    pendingApprovalItems: 0,
    efficiency: 0
  });

  // الترجمات الكاملة
  const t = {
    ar: {
      title: 'لوحة تحكم الشيف',
      welcome: 'مرحباً',
      refresh: 'تحديث',
      newOrder: 'طلب جديد',
      totalTasks: 'إجمالي المهام',
      inProgress: 'قيد التنفيذ',
      completed: 'مكتملة',
      efficiency: 'الكفاءة',
      assignedTasks: 'المهام المعينة',
      myOrders: 'طلباتي',
      noTasks: 'لا توجد مهام معينة',
      start: 'بدء الإنتاج',
      reject: 'رفض',
      complete: 'إكمال',
      started: 'بدأ',
      rejectionReason: 'سبب الرفض',
      rejectItem: 'رفض العنصر',
      cancel: 'إلغاء',
      createOrder: 'إنشاء طلب إنتاج جديد',
      products: 'المنتجات',
      addProduct: 'إضافة منتج',
      priority: 'الأولوية',
      low: 'منخفض',
      medium: 'متوسط',
      high: 'مرتفع',
      urgent: 'عاجل',
      notes: 'ملاحظات (اختياري)',
      creating: 'جاري الإنشاء',
      selectProduct: 'اختر المنتج',
      quantity: 'الكمية',
      remove: 'إزالة',
      invalidQuantity: 'الكمية يجب أن تكون أكبر من 0',
      invalidProduct: 'يجب اختيار منتج',
      orderCreated: 'تم إنشاء الطلب بنجاح',
      orderError: 'خطأ في إنشاء الطلب',
      itemStarted: 'تم بدء إنتاج العنصر',
      itemCompleted: 'تم إكمال العنصر',
      itemRejected: 'تم رفض العنصر',
      itemError: 'خطأ في العملية',
      fetchError: 'خطأ في جلب البيانات',
      departmentsWarning: 'ليس لديك أقسام معينة. يرجى الاتصال بالإدارة.',
      productNotFound: 'لم يتم العثور على المنتج',
      productAdded: 'تمت إضافة المنتج',
      assignedTo: 'معين إلى',
      orderNumber: 'رقم الطلب',
      createdAt: 'تم الإنشاء في',
      startProduction: 'بدء الإنتاج',
      completeTask: 'إكمال المهمة',
      taskNotes: 'ملاحظات المهمة',
      optional: '(اختياري)',
      waiting: 'قيد الانتظار',
      inProduction: 'في الإنتاج',
      approved: 'معتمد',
      pendingApproval: 'بانتظار الموافقة',
      waitingForApproval: 'بانتظار موافقة الإدارة',
      stocked: 'مخزن',
      cancelled: 'ملغى',
      viewDetails: 'عرض التفاصيل',
      close: 'إغلاق',
      confirm: 'تأكيد',
      save: 'حفظ',
      edit: 'تعديل',
      delete: 'حذف',
      filter: 'تصفية',
      search: 'بحث',
      all: 'الكل',
      today: 'اليوم',
      thisWeek: 'هذا الأسبوع',
      thisMonth: 'هذا الشهر',
      lastUpdated: 'آخر تحديث',
      status: 'الحالة',
      actions: 'الإجراءات',
      noData: 'لا توجد بيانات',
      loading: 'جاري التحميل...',
      error: 'خطأ',
      success: 'نجاح',
      warning: 'تحذير',
      info: 'معلومات'
    },
    en: {
      title: 'Chef Dashboard',
      welcome: 'Welcome',
      refresh: 'Refresh',
      newOrder: 'New Order',
      totalTasks: 'Total Tasks',
      inProgress: 'In Progress',
      completed: 'Completed',
      efficiency: 'Efficiency',
      assignedTasks: 'Assigned Tasks',
      myOrders: 'My Orders',
      noTasks: 'No assigned tasks',
      start: 'Start Production',
      reject: 'Reject',
      complete: 'Complete',
      started: 'Started',
      rejectionReason: 'Rejection Reason',
      rejectItem: 'Reject Item',
      cancel: 'Cancel',
      createOrder: 'Create New Production Order',
      products: 'Products',
      addProduct: 'Add Product',
      priority: 'Priority',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      urgent: 'Urgent',
      notes: 'Notes (Optional)',
      creating: 'Creating',
      selectProduct: 'Select Product',
      quantity: 'Quantity',
      remove: 'Remove',
      invalidQuantity: 'Quantity must be greater than 0',
      invalidProduct: 'Please select a product',
      orderCreated: 'Order created successfully',
      orderError: 'Error creating order',
      itemStarted: 'Item production started',
      itemCompleted: 'Item completed',
      itemRejected: 'Item rejected',
      itemError: 'Error in operation',
      fetchError: 'Error fetching data',
      departmentsWarning: 'No departments assigned. Please contact administration.',
      productNotFound: 'Product not found',
      productAdded: 'Product added',
      assignedTo: 'Assigned to',
      orderNumber: 'Order Number',
      createdAt: 'Created at',
      startProduction: 'Start Production',
      completeTask: 'Complete Task',
      taskNotes: 'Task Notes',
      optional: '(Optional)',
      waiting: 'Waiting',
      inProduction: 'In Production',
      approved: 'Approved',
      pendingApproval: 'Pending Approval',
      waitingForApproval: 'Waiting for admin approval',
      stocked: 'Stocked',
      cancelled: 'Cancelled',
      viewDetails: 'View Details',
      close: 'Close',
      confirm: 'Confirm',
      save: 'Save',
      edit: 'Edit',
      delete: 'Delete',
      filter: 'Filter',
      search: 'Search',
      all: 'All',
      today: 'Today',
      thisWeek: 'This Week',
      thisMonth: 'This Month',
      lastUpdated: 'Last Updated',
      status: 'Status',
      actions: 'Actions',
      noData: 'No data',
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      warning: 'Warning',
      info: 'Info'
    }
  };

  // 🔧 دالة مساعدة لتحويل الحالة إلى نوع متوافق
  const parseItemStatus = (item: any): ChefOrderItem['status'] => {
    const status = String(item.status || '').toLowerCase().trim();
    
    // تحويل الحالات من الموديل الخلفي إلى واجهة متوافقة
    const statusMap: Record<string, ChefOrderItem['status']> = {
      'pending_approval': 'pending_approval',
      'pending': 'pending_approval',
      'awaiting_approval': 'pending_approval',
      'بانتظار الموافقة': 'pending_approval',
      'قيد الانتظار': 'pending_approval',
      
      'approved': 'approved',
      'معتمد': 'approved',
      'موافق': 'approved',
      
      'assigned': 'assigned',
      'معين': 'assigned',
      'مكلف': 'assigned',
      
      'in_progress': 'in_progress',
      'in_production': 'in_progress',
      'قيد التنفيذ': 'in_progress',
      'في الإنتاج': 'in_progress',
      'جاري التنفيذ': 'in_progress',
      
      'completed': 'completed',
      'مكتمل': 'completed',
      'منتهي': 'completed',
      
      'rejected': 'rejected',
      'مرفوض': 'rejected',
      'مرفوضة': 'rejected'
    };
    
    return statusMap[status] || 'pending_approval';
  };

  // 🔧 دالة جلب البيانات المحسنة
  const fetchData = useCallback(async () => {
    if (!user || user.role !== 'chef') return;

    try {
      setLoading(true);
      
      // جلب جميع البيانات في وقت واحد
      const [ordersRes, itemsRes, productsRes] = await Promise.allSettled([
        factoryOrdersAPI.getChefOrders(),
        factoryOrdersAPI.getAssignedItems(),
        productsAPI.getAll({ 
          limit: 100, 
          lang: language, 
          isActive: true,
          withDepartments: true 
        })
      ]);

      let ordersData: FactoryOrder[] = [];
      let itemsData: ChefOrderItem[] = [];
      let productsData: Product[] = [];

      // معالجة استجابة الطلبات
      if (ordersRes.status === 'fulfilled') {
        const response = ordersRes.value;
        
        // تنسيق البيانات من الاستجابة
        if (response?.data?.data) {
          ordersData = Array.isArray(response.data.data) ? response.data.data : [];
        } else if (response?.data) {
          ordersData = Array.isArray(response.data) ? response.data : [];
        } else if (Array.isArray(response)) {
          ordersData = response;
        }
        
        console.log('✅ الطلبات المحملة:', ordersData.length);
        setOrders(ordersData);
      }

      // معالجة استجابة العناصر القابلة للتنفيذ
      if (itemsRes.status === 'fulfilled') {
        const response = itemsRes.value;
        
        try {
          // استخراج البيانات من الاستجابة
          let rawData: any = null;
          
          if (response?.data?.data) {
            rawData = response.data.data;
          } else if (response?.data) {
            rawData = response.data;
          } else {
            rawData = response;
          }

          // دمج جميع العناصر من التصنيفات المختلفة
          const allItems: any[] = [];
          
          if (rawData?.pendingApproval) {
            rawData.pendingApproval.forEach((item: any) => {
              allItems.push({ ...item, category: 'pendingApproval' });
            });
          }
          
          if (rawData?.readyToStart) {
            rawData.readyToStart.forEach((item: any) => {
              allItems.push({ ...item, category: 'readyToStart' });
            });
          }
          
          if (rawData?.inProgress) {
            rawData.inProgress.forEach((item: any) => {
              allItems.push({ ...item, category: 'inProgress' });
            });
          }
          
          if (rawData?.completed) {
            rawData.completed.forEach((item: any) => {
              allItems.push({ ...item, category: 'completed' });
            });
          }
          
          if (rawData?.rejected) {
            rawData.rejected.forEach((item: any) => {
              allItems.push({ ...item, category: 'rejected' });
            });
          }

          // تحويل البيانات إلى تنسيق متوافق
          itemsData = allItems.map((item: any) => {
            const parsedStatus = parseItemStatus(item);
            
            return {
              _id: item._id || item.itemId || `item-${Date.now()}-${Math.random()}`,
              orderId: item.orderId || item.order?._id || '',
              orderNumber: item.orderNumber || item.order?.orderNumber || 'N/A',
              itemId: item.itemId || item._id || '',
              product: item.product || { _id: '', name: '', unit: '' },
              quantity: item.quantity || item.approvedQuantity || 1,
              approvedQuantity: item.approvedQuantity || item.quantity || 1,
              originalQuantity: item.originalQuantity || item.quantity || 1,
              status: parsedStatus,
              assignedTo: item.assignedTo || null,
              startedAt: item.startedAt,
              completedAt: item.completedAt,
              rejectReason: item.rejectReason,
              notes: item.notes,
              adminNotes: item.adminNotes,
              requiresApproval: item.requiresApproval || false,
              orderCreatedAt: item.orderCreatedAt || item.createdAt || item.order?.createdAt || new Date().toISOString(),
              orderPriority: item.orderPriority || item.priority || 'medium',
              orderStatus: item.orderStatus || item.status || 'pending'
            };
          });
          
          console.log('✅ العناصر القابلة للتنفيذ:', itemsData.length);
          setActionableItems(itemsData);
          
          // حساب الإحصائيات
          const assignedCount = itemsData.filter(item => 
            item.status === 'approved' || item.status === 'assigned'
          ).length;
          
          const inProgressCount = itemsData.filter(item => 
            item.status === 'in_progress'
          ).length;
          
          const completedCount = itemsData.filter(item => 
            item.status === 'completed'
          ).length;
          
          const pendingApprovalCount = itemsData.filter(item => 
            item.status === 'pending_approval'
          ).length;
          
          const totalTasks = assignedCount + inProgressCount + completedCount + pendingApprovalCount;
          const efficiency = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;
          
          setStats({
            totalTasks,
            assignedItems: assignedCount,
            inProgressItems: inProgressCount,
            completedItems: completedCount,
            pendingApprovalItems: pendingApprovalCount,
            efficiency
          });
          
        } catch (parseError) {
          console.error('❌ خطأ في تحليل بيانات العناصر:', parseError);
          toast.error(t[language].fetchError, {
            position: isRtl ? 'top-right' : 'top-left'
          });
        }
      }

      // معالجة استجابة المنتجات
      if (productsRes.status === 'fulfilled') {
        const response = productsRes.value;
        
        if (response?.data) {
          productsData = Array.isArray(response.data) ? response.data : [];
        } else if (Array.isArray(response)) {
          productsData = response;
        }
        
        // جلب أقسام الشيف من localStorage أو API
        const chefDepartments = JSON.parse(localStorage.getItem('chefDepartments') || '[]');
        const deptIds = chefDepartments.map((dept: any) => dept._id || dept);
        
        // فلترة المنتجات حسب أقسام الشيف
        let filteredProducts = productsData;
        if (deptIds.length > 0) {
          filteredProducts = productsData.filter((product: Product) => {
            const productDeptId = product.department?._id;
            return productDeptId && deptIds.includes(productDeptId.toString());
          });
        }
        
        console.log('✅ المنتجات المتاحة:', filteredProducts.length);
        setAvailableProducts(filteredProducts);
        
        if (deptIds.length === 0 && filteredProducts.length === 0) {
          toast.info(t[language].departmentsWarning, {
            position: isRtl ? 'top-right' : 'top-left'
          });
        }
      }

      // معالجة الأخطاء
      const errors = [ordersRes, itemsRes, productsRes]
        .filter(result => result.status === 'rejected')
        .map((result: any) => result.reason?.message || 'Unknown error');

      if (errors.length > 0) {
        console.error('❌ أخطاء في جلب البيانات:', errors);
        toast.error(`${t[language].fetchError}: ${errors.join(', ')}`, {
          position: isRtl ? 'top-right' : 'top-left'
        });
      }

    } catch (error: any) {
      console.error('❌ خطأ عام في جلب البيانات:', error);
      toast.error(`${t[language].fetchError}: ${error.message || 'Unknown'}`, {
        position: isRtl ? 'top-right' : 'top-left'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user, language, isRtl]);

  // 🔧 بدء إنتاج العنصر
  const handleStartItem = async (orderId: string, itemId: string) => {
    try {
      setActionLoading(`start-${orderId}-${itemId}`);
      
      await factoryOrdersAPI.startItem(orderId, itemId);
      
      toast.success(t[language].itemStarted, {
        position: isRtl ? 'top-right' : 'top-left'
      });
      
      // تحديث البيانات بعد نجاح العملية
      setTimeout(fetchData, 300);
      
    } catch (error: any) {
      console.error('❌ خطأ في بدء الإنتاج:', error);
      toast.error(`${t[language].itemError}: ${error.message}`, {
        position: isRtl ? 'top-right' : 'top-left'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // 🔧 إكمال العنصر
  const handleCompleteItem = async (orderId: string, itemId: string, notes?: string) => {
    try {
      setActionLoading(`complete-${orderId}-${itemId}`);
      
      await factoryOrdersAPI.completeItem(orderId, itemId, { notes });
      
      toast.success(t[language].itemCompleted, {
        position: isRtl ? 'top-right' : 'top-left'
      });
      
      // مسح ملاحظات الإكمال
      setCompletionNotes(prev => ({ ...prev, [itemId]: '' }));
      
      // تحديث البيانات
      setTimeout(fetchData, 300);
      
    } catch (error: any) {
      console.error('❌ خطأ في إكمال العنصر:', error);
      toast.error(`${t[language].itemError}: ${error.message}`, {
        position: isRtl ? 'top-right' : 'top-left'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // 🔧 رفض العنصر
  const handleRejectItem = async () => {
    if (!rejectReason.trim() || rejectReason.trim().length < 3) {
      toast.error(t[language].rejectionReason + ' ' + 
        (isRtl ? 'يجب أن يكون 3 أحرف على الأقل' : 'must be at least 3 characters'), {
        position: isRtl ? 'top-right' : 'top-left'
      });
      return;
    }

    if (!rejectModal.item) return;

    try {
      setActionLoading('reject-item');
      
      const { orderId, itemId } = rejectModal.item;
      
      // استدعاء API للرفض
      await factoryOrdersAPI.rejectItem(orderId, itemId, { rejectReason });
      
      toast.success(t[language].itemRejected, {
        position: isRtl ? 'top-right' : 'top-left'
      });
      
      // إغلاق المودال وإعادة تعيين البيانات
      setRejectModal({ open: false, item: null });
      setRejectReason('');
      
      // تحديث البيانات
      setTimeout(fetchData, 300);
      
    } catch (error: any) {
      console.error('❌ خطأ في الرفض:', error);
      toast.error(`${t[language].itemError}: ${error.message}`, {
        position: isRtl ? 'top-right' : 'top-left'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // 🔧 إنشاء طلب جديد
  const handleCreateOrder = async () => {
    try {
      // التحقق من صحة العناصر
      const validItems = newOrder.items.filter(item => 
        item.product && isValidObjectId(item.product) && item.quantity > 0
      );
      
      if (validItems.length === 0) {
        toast.error(isRtl ? 'الرجاء إضافة عنصر واحد على الأقل' : 'Please add at least one item', {
          position: isRtl ? 'top-right' : 'top-left'
        });
        return;
      }

      setActionLoading('create-order');
      
      // تحضير بيانات الطلب
      const orderData = {
        orderNumber: newOrder.orderNumber,
        items: validItems.map(item => ({
          product: item.product,
          quantity: item.quantity,
          notes: item.notes || ''
        })),
        notes: newOrder.notes.trim(),
        priority: newOrder.priority
      };

      // استدعاء API لإنشاء الطلب
      await factoryOrdersAPI.create(orderData);
      
      toast.success(t[language].orderCreated, {
        position: isRtl ? 'top-right' : 'top-left'
      });
      
      // إعادة تعيين النموذج
      setNewOrder({
        items: [{ product: '', quantity: 1 }],
        notes: '',
        priority: 'medium',
        orderNumber: `PROD-${Date.now().toString().slice(-8)}`
      });
      setCreateModal(false);
      
      // تحديث البيانات
      setTimeout(fetchData, 500);
      
    } catch (error: any) {
      console.error('❌ خطأ في إنشاء الطلب:', error);
      toast.error(`${t[language].orderError}: ${error.message}`, {
        position: isRtl ? 'top-right' : 'top-left'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // 🔧 إضافة عنصر جديد للنموذج
  const addOrderItem = () => {
    setNewOrder(prev => ({
      ...prev,
      items: [...prev.items, { product: '', quantity: 1 }]
    }));
  };

  // 🔧 تحديث عنصر في النموذج
  const updateOrderItem = (index: number, field: 'product' | 'quantity' | 'notes', value: string) => {
    const updatedItems = [...newOrder.items];
    
    if (field === 'product') {
      updatedItems[index] = { ...updatedItems[index], product: value };
    } else if (field === 'quantity') {
      const qty = parseInt(value) || 0;
      updatedItems[index] = { ...updatedItems[index], quantity: qty > 0 ? qty : 1 };
    } else if (field === 'notes') {
      updatedItems[index] = { ...updatedItems[index], notes: value };
    }
    
    setNewOrder(prev => ({ ...prev, items: updatedItems }));
  };

  // 🔧 إزالة عنصر من النموذج
  const removeOrderItem = (index: number) => {
    if (newOrder.items.length > 1) {
      const updatedItems = newOrder.items.filter((_, i) => i !== index);
      setNewOrder(prev => ({ ...prev, items: updatedItems }));
    }
  };

  // 🔧 تحديث ملاحظات الإكمال
  const updateCompletionNotes = (itemId: string, notes: string) => {
    setCompletionNotes(prev => ({ ...prev, [itemId]: notes }));
  };

  // 🔧 استمع إلى تحديثات Socket
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleFactoryItemStatusChanged = ({ orderId, itemId }: { orderId: string; itemId: string }) => {
      console.log('🔄 تحديث حالة العنصر:', { orderId, itemId });
      fetchData();
    };

    const handleFactoryOrderCreated = ({ orderId }: { orderId: string }) => {
      console.log('🔄 إنشاء طلب جديد:', orderId);
      fetchData();
    };

    const handleFactoryTaskAssigned = ({ orderId }: { orderId: string }) => {
      console.log('🔄 تعيين مهمة جديدة:', orderId);
      fetchData();
    };

    socket.on('factoryItemStatusChanged', handleFactoryItemStatusChanged);
    socket.on('factoryOrderCreated', handleFactoryOrderCreated);
    socket.on('factoryTaskAssigned', handleFactoryTaskAssigned);

    return () => {
      socket.off('factoryItemStatusChanged', handleFactoryItemStatusChanged);
      socket.off('factoryOrderCreated', handleFactoryOrderCreated);
      socket.off('factoryTaskAssigned', handleFactoryTaskAssigned);
    };
  }, [socket, isConnected, fetchData]);

  // 🔧 تأثير جلب البيانات الأولي
  useEffect(() => {
    if (user?.role === 'chef') {
      fetchData();
    }
  }, [user, fetchData]);

  // 🔧 تحديث دوري للبيانات
  useEffect(() => {
    const interval = setInterval(() => {
      if (user?.role === 'chef' && !loading && isConnected) {
        fetchData();
      }
    }, 30000); // كل 30 ثانية
    
    return () => clearInterval(interval);
  }, [user, fetchData, loading, isConnected]);

  // 🔧 دوال المساعدة المحسنة
  const getStatusText = (status: string): string => {
    const statusMap: Record<string, string> = {
      'pending_approval': isRtl ? 'بانتظار الموافقة' : 'Pending Approval',
      'pending': isRtl ? 'قيد الانتظار' : 'Pending',
      'approved': isRtl ? 'معتمد' : 'Approved',
      'assigned': isRtl ? 'معين' : 'Assigned',
      'in_progress': isRtl ? 'قيد التنفيذ' : 'In Progress',
      'completed': isRtl ? 'مكتمل' : 'Completed',
      'rejected': isRtl ? 'مرفوض' : 'Rejected',
      'requested': isRtl ? 'مطلوب' : 'Requested',
      'in_production': isRtl ? 'في الإنتاج' : 'In Production',
      'stocked': isRtl ? 'مخزن' : 'Stocked',
      'cancelled': isRtl ? 'ملغى' : 'Cancelled',
      'draft': isRtl ? 'مسودة' : 'Draft'
    };
    
    return statusMap[status] || status;
  };

  const getPriorityText = (priority: string): string => {
    const priorityMap = {
      'urgent': isRtl ? 'عاجل' : 'Urgent',
      'high': isRtl ? 'مرتفع' : 'High',
      'medium': isRtl ? 'متوسط' : 'Medium',
      'low': isRtl ? 'منخفض' : 'Low'
    };
    return priorityMap[priority as keyof typeof priorityMap] || priority;
  };

  const getStatusColor = (status: string): string => {
    const colorMap = {
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'in_progress': 'bg-blue-100 text-blue-800 border-blue-200',
      'assigned': 'bg-amber-100 text-amber-800 border-amber-200',
      'approved': 'bg-teal-100 text-teal-800 border-teal-200',
      'pending_approval': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'rejected': 'bg-red-100 text-red-800 border-red-200',
      'requested': 'bg-purple-100 text-purple-800 border-purple-200',
      'in_production': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'stocked': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'cancelled': 'bg-gray-100 text-gray-800 border-gray-200',
      'draft': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    
    return colorMap[status as keyof typeof colorMap] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPriorityColor = (priority: string): string => {
    const colorMap = {
      'urgent': 'bg-red-100 text-red-800',
      'high': 'bg-orange-100 text-orange-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800'
    };
    return colorMap[priority as keyof typeof colorMap] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string): string => {
    try {
      if (!dateString) return '';
      const date = new Date(dateString);
      
      if (isRtl) {
        return date.toLocaleDateString('ar-SA', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    } catch (error) {
      return dateString;
    }
  };

  const getProductDisplayName = (product: any): string => {
    if (!product) return isRtl ? 'غير معروف' : 'Unknown';
    return isRtl ? product.name : (product.nameEn || product.name);
  };

  const getProductDisplayUnit = (product: any): string => {
    if (!product) return isRtl ? 'وحدة' : 'unit';
    return isRtl ? product.unit : (product.unitEn || product.unit);
  };

  // 🔧 التصميم أثناء التحميل
  if (loading && !refreshing) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {isRtl ? 'جاري تحميل بيانات الشيف...' : 'Loading chef data...'}
          </h3>
          <p className="text-sm text-gray-600">
            {isRtl ? 'يرجى الانتظار...' : 'Please wait...'}
          </p>
        </motion.div>
      </div>
    );
  }

  // 🔧 إذا لم يكن المستخدم شيفاً
  if (user?.role !== 'chef') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <ChefHat className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {isRtl ? 'غير مصرح بالوصول' : 'Access Denied'}
          </h3>
          <p className="text-gray-600 max-w-md mx-auto">
            {isRtl
              ? 'هذه الصفحة للشيفات فقط. يرجى تسجيل الدخول بحساب شيف.'
              : 'This page is for chefs only. Please login with a chef account.'
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white shadow-sm border-b"
      >
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <ChefHat className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {t[language].title}
                </h1>
                <p className="text-sm text-gray-600">
                  {t[language].welcome}, <span className="font-semibold text-amber-700">{user?.name}</span>
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setRefreshing(true);
                  fetchData();
                }}
                disabled={refreshing}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  refreshing
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                    : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                }`}
              >
                {refreshing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                <span className="text-sm font-medium">
                  {t[language].refresh}
                </span>
              </button>
              
              <button
                onClick={() => setCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors shadow-sm"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {t[language].newOrder}
                </span>
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="px-4 sm:px-6 lg:px-8 py-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* إجمالي المهام */}
          <div className="bg-white rounded-xl shadow-sm border p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t[language].totalTasks}</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalTasks}</p>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg">
                <BarChart className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {isRtl ? 'جميع المهام المعينة لك' : 'All tasks assigned to you'}
            </div>
          </div>
          
          {/* قيد التنفيذ */}
          <div className="bg-white rounded-xl shadow-sm border p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t[language].inProgress}</p>
                <p className="text-2xl font-bold text-blue-600">{stats.inProgressItems}</p>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg">
                <PlayCircle className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {isRtl ? 'المهام قيد الإنتاج' : 'Tasks in production'}
            </div>
          </div>
          
          {/* مكتملة */}
          <div className="bg-white rounded-xl shadow-sm border p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t[language].completed}</p>
                <p className="text-2xl font-bold text-green-600">{stats.completedItems}</p>
              </div>
              <div className="p-2 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {isRtl ? 'المهام المكتملة' : 'Completed tasks'}
            </div>
          </div>
          
          {/* الكفاءة */}
          <div className="bg-white rounded-xl shadow-sm border p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t[language].efficiency}</p>
                <div className="flex items-baseline gap-1">
                  <p className="text-2xl font-bold text-purple-600">{stats.efficiency}</p>
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>
              <div className="p-2 bg-purple-50 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {isRtl ? 'معدل إنجاز المهام' : 'Task completion rate'}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border mb-6 overflow-hidden">
          <div className="border-b">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('assigned')}
                className={`flex-1 py-4 px-6 text-center text-sm font-medium transition-colors relative ${
                  activeTab === 'assigned'
                    ? 'text-amber-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <ListChecks className="w-4 h-4" />
                  {t[language].assignedTasks}
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    activeTab === 'assigned'
                      ? 'bg-amber-100 text-amber-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {actionableItems.length}
                  </span>
                </div>
                {activeTab === 'assigned' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500"></div>
                )}
              </button>
              
              <button
                onClick={() => setActiveTab('orders')}
                className={`flex-1 py-4 px-6 text-center text-sm font-medium transition-colors relative ${
                  activeTab === 'orders'
                    ? 'text-amber-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <FileText className="w-4 h-4" />
                  {t[language].myOrders}
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    activeTab === 'orders'
                      ? 'bg-amber-100 text-amber-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {orders.length}
                  </span>
                </div>
                {activeTab === 'orders' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500"></div>
                )}
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="mt-6">
          {activeTab === 'assigned' && (
            <AssignedItemsTab 
              items={actionableItems}
              onStart={handleStartItem}
              onComplete={handleCompleteItem}
              onReject={(item) => setRejectModal({ open: true, item })}
              onUpdateNotes={updateCompletionNotes}
              completionNotes={completionNotes}
              actionLoading={actionLoading}
              isRtl={isRtl}
              formatDate={formatDate}
              getStatusText={getStatusText}
              getStatusColor={getStatusColor}
              getProductDisplayName={getProductDisplayName}
              getProductDisplayUnit={getProductDisplayUnit}
              t={t[language]}
            />
          )}
          
          {activeTab === 'orders' && (
            <OrdersTab 
              orders={orders}
              isRtl={isRtl}
              formatDate={formatDate}
              getStatusText={getStatusText}
              getStatusColor={getStatusColor}
              getPriorityText={getPriorityText}
              getPriorityColor={getPriorityColor}
              getProductDisplayName={getProductDisplayName}
              getProductDisplayUnit={getProductDisplayUnit}
              t={t[language]}
            />
          )}
        </div>
      </motion.div>

      {/* Reject Modal */}
      <RejectModal 
        isOpen={rejectModal.open}
        onClose={() => {
          setRejectModal({ open: false, item: null });
          setRejectReason('');
        }}
        onConfirm={handleRejectItem}
        reason={rejectReason}
        onChangeReason={setRejectReason}
        loading={actionLoading === 'reject-item'}
        isRtl={isRtl}
        t={t[language]}
      />

      {/* Create Order Modal */}
      <CreateOrderModal 
        isOpen={createModal}
        onClose={() => setCreateModal(false)}
        onConfirm={handleCreateOrder}
        order={newOrder}
        products={availableProducts}
        onAddItem={addOrderItem}
        onUpdateItem={updateOrderItem}
        onRemoveItem={removeOrderItem}
        onOrderChange={setNewOrder}
        loading={actionLoading === 'create-order'}
        isRtl={isRtl}
        t={t[language]}
        language={language}
        getProductDisplayName={getProductDisplayName}
      />
    </div>
  );
};

// ========== Component: Assigned Items Tab ==========
interface AssignedItemsTabProps {
  items: ChefOrderItem[];
  onStart: (orderId: string, itemId: string) => void;
  onComplete: (orderId: string, itemId: string, notes?: string) => void;
  onReject: (item: ChefOrderItem) => void;
  onUpdateNotes: (itemId: string, notes: string) => void;
  completionNotes: Record<string, string>;
  actionLoading: string | null;
  isRtl: boolean;
  formatDate: (dateString: string) => string;
  getStatusText: (status: string) => string;
  getStatusColor: (status: string) => string;
  getProductDisplayName: (product: any) => string;
  getProductDisplayUnit: (product: any) => string;
  t: any;
}

const AssignedItemsTab: React.FC<AssignedItemsTabProps> = ({ 
  items, 
  onStart, 
  onComplete, 
  onReject, 
  onUpdateNotes,
  completionNotes,
  actionLoading,
  isRtl,
  formatDate,
  getStatusText,
  getStatusColor,
  getProductDisplayName,
  getProductDisplayUnit,
  t
}) => {
  // تصنيف العناصر حسب الحالة
  const assignedItems = items.filter(item => 
    item.status === 'assigned' || item.status === 'approved'
  );
  
  const inProgressItems = items.filter(item => 
    item.status === 'in_progress'
  );
  
  const completedItems = items.filter(item => 
    item.status === 'completed'
  );
  
  const pendingApprovalItems = items.filter(item => 
    item.status === 'pending_approval'
  );
  
  const rejectedItems = items.filter(item => 
    item.status === 'rejected'
  );

  if (items.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-12 bg-white rounded-xl shadow-sm border"
      >
        <div className="p-4 bg-amber-50 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
          <ChefHat className="w-10 h-10 text-amber-600" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t.noTasks}
        </h3>
        <p className="text-gray-600 max-w-md mx-auto mb-6">
          {isRtl
            ? 'لا توجد مهام معينة لك حالياً. انتظر حتى يتم تعيين مهام جديدة لك أو أنشئ طلباً جديداً.'
            : 'No tasks are currently assigned to you. Wait for new tasks to be assigned or create a new order.'
          }
        </p>
        <div className="text-sm text-gray-500">
          {isRtl ? 'آخر تحديث: الآن' : 'Last updated: Just now'}
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-8">
      {/* العناصر المعينة والجاهزة للبدء */}
      {(assignedItems.length > 0 || pendingApprovalItems.length > 0) && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-600" />
              {t.assignedTasks}
            </h3>
            <div className="flex gap-2">
              {assignedItems.length > 0 && (
                <span className="px-2 py-1 bg-amber-100 text-amber-600 text-xs font-medium rounded-full">
                  {assignedItems.length} {isRtl ? 'جاهزة' : 'ready'}
                </span>
              )}
              {pendingApprovalItems.length > 0 && (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-600 text-xs font-medium rounded-full">
                  {pendingApprovalItems.length} {isRtl ? 'بانتظار' : 'pending'}
                </span>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {assignedItems.map((item) => (
              <AssignedItemCard 
                key={item._id}
                item={item}
                onStart={onStart}
                onReject={onReject}
                actionLoading={actionLoading}
                isRtl={isRtl}
                formatDate={formatDate}
                getStatusText={getStatusText}
                getStatusColor={getStatusColor}
                getProductDisplayName={getProductDisplayName}
                getProductDisplayUnit={getProductDisplayUnit}
                t={t}
              />
            ))}
            
            {pendingApprovalItems.map((item) => (
              <PendingApprovalCard 
                key={item._id}
                item={item}
                isRtl={isRtl}
                formatDate={formatDate}
                getStatusText={getStatusText}
                getStatusColor={getStatusColor}
                getProductDisplayName={getProductDisplayName}
                getProductDisplayUnit={getProductDisplayUnit}
                t={t}
              />
            ))}
          </div>
        </div>
      )}

      {/* العناصر قيد التنفيذ */}
      {inProgressItems.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <PlayCircle className="w-5 h-5 text-blue-600" />
              {t.inProgress}
            </h3>
            <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs font-medium rounded-full">
              {inProgressItems.length}
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {inProgressItems.map((item) => (
              <InProgressItemCard 
                key={item._id}
                item={item}
                onComplete={onComplete}
                onReject={onReject}
                onUpdateNotes={onUpdateNotes}
                completionNotes={completionNotes}
                actionLoading={actionLoading}
                isRtl={isRtl}
                formatDate={formatDate}
                getStatusText={getStatusText}
                getStatusColor={getStatusColor}
                getProductDisplayName={getProductDisplayName}
                getProductDisplayUnit={getProductDisplayUnit}
                t={t}
              />
            ))}
          </div>
        </div>
      )}

      {/* العناصر المكتملة */}
      {completedItems.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              {t.completed}
            </h3>
            <span className="px-2 py-1 bg-green-100 text-green-600 text-xs font-medium rounded-full">
              {completedItems.length}
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {completedItems.slice(0, 6).map((item) => (
              <CompletedItemCard 
                key={item._id}
                item={item}
                isRtl={isRtl}
                formatDate={formatDate}
                getStatusText={getStatusText}
                getStatusColor={getStatusColor}
                getProductDisplayName={getProductDisplayName}
                getProductDisplayUnit={getProductDisplayUnit}
                t={t}
              />
            ))}
          </div>
        </div>
      )}

      {/* العناصر المرفوضة */}
      {rejectedItems.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              {isRtl ? 'المرفوضة' : 'Rejected'}
            </h3>
            <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-medium rounded-full">
              {rejectedItems.length}
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rejectedItems.map((item) => (
              <RejectedItemCard 
                key={item._id}
                item={item}
                isRtl={isRtl}
                formatDate={formatDate}
                getStatusText={getStatusText}
                getStatusColor={getStatusColor}
                getProductDisplayName={getProductDisplayName}
                getProductDisplayUnit={getProductDisplayUnit}
                t={t}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ========== Component: Assigned Item Card ==========
interface AssignedItemCardProps {
  item: ChefOrderItem;
  onStart: (orderId: string, itemId: string) => void;
  onReject: (item: ChefOrderItem) => void;
  actionLoading: string | null;
  isRtl: boolean;
  formatDate: (dateString: string) => string;
  getStatusText: (status: string) => string;
  getStatusColor: (status: string) => string;
  getProductDisplayName: (product: any) => string;
  getProductDisplayUnit: (product: any) => string;
  t: any;
}

const AssignedItemCard: React.FC<AssignedItemCardProps> = ({
  item,
  onStart,
  onReject,
  actionLoading,
  isRtl,
  formatDate,
  getStatusText,
  getStatusColor,
  getProductDisplayName,
  getProductDisplayUnit,
  t
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow"
    >
      <div className="p-4 border-b bg-amber-50">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-bold text-gray-900">{item.orderNumber}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                {getStatusText(item.status)}
              </span>
            </div>
            <p className="text-xs text-gray-600">
              {formatDate(item.orderCreatedAt)}
            </p>
          </div>
          <div className="text-right">
            <span className={`px-2 py-1 rounded text-xs font-medium ${item.orderPriority === 'urgent' ? 'bg-red-100 text-red-800' : 
              item.orderPriority === 'high' ? 'bg-orange-100 text-orange-800' :
              item.orderPriority === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
              {item.orderPriority === 'urgent' ? (isRtl ? 'عاجل' : 'Urgent') :
               item.orderPriority === 'high' ? (isRtl ? 'مرتفع' : 'High') :
               item.orderPriority === 'medium' ? (isRtl ? 'متوسط' : 'Medium') : (isRtl ? 'منخفض' : 'Low')}
            </span>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="space-y-3">
          <div>
            <p className="font-medium text-gray-900">
              {getProductDisplayName(item.product)}
            </p>
            <div className="flex items-center gap-4 mt-1">
              <p className="text-sm text-gray-600">
                {item.quantity} {getProductDisplayUnit(item.product)}
              </p>
              {item.approvedQuantity !== item.quantity && (
                <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                  {isRtl ? 'معتمد:' : 'Approved:'} {item.approvedQuantity}
                </p>
              )}
            </div>
          </div>

          {item.assignedTo && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <User className="w-4 h-4" />
              <span>{t.assignedTo}:</span>
              <span className="font-medium">
                {isRtl ? item.assignedTo.name : (item.assignedTo.nameEn || item.assignedTo.name)}
              </span>
            </div>
          )}

          {item.notes && (
            <div className="text-sm text-gray-500 bg-gray-50 p-2 rounded">
              <span className="font-medium">{t.notes}:</span> {item.notes}
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={() => onStart(item.orderId, item.itemId)}
            disabled={actionLoading === `start-${item.orderId}-${item.itemId}`}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 transition-colors"
          >
            {actionLoading === `start-${item.orderId}-${item.itemId}` ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            {t.startProduction}
          </button>
          
          <button
            onClick={() => onReject(item)}
            className="px-3 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm font-medium transition-colors"
          >
            {t.reject}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ========== Component: In Progress Item Card ==========
interface InProgressItemCardProps {
  item: ChefOrderItem;
  onComplete: (orderId: string, itemId: string, notes?: string) => void;
  onReject: (item: ChefOrderItem) => void;
  onUpdateNotes: (itemId: string, notes: string) => void;
  completionNotes: Record<string, string>;
  actionLoading: string | null;
  isRtl: boolean;
  formatDate: (dateString: string) => string;
  getStatusText: (status: string) => string;
  getStatusColor: (status: string) => string;
  getProductDisplayName: (product: any) => string;
  getProductDisplayUnit: (product: any) => string;
  t: any;
}

const InProgressItemCard: React.FC<InProgressItemCardProps> = ({
  item,
  onComplete,
  onReject,
  onUpdateNotes,
  completionNotes,
  actionLoading,
  isRtl,
  formatDate,
  getStatusText,
  getStatusColor,
  getProductDisplayName,
  getProductDisplayUnit,
  t
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow"
    >
      <div className="p-4 border-b bg-blue-50">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-bold text-gray-900">{item.orderNumber}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                {getStatusText(item.status)}
              </span>
            </div>
            <p className="text-xs text-gray-600">
              {formatDate(item.orderCreatedAt)}
            </p>
          </div>
          {item.startedAt && (
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDate(item.startedAt)}
            </div>
          )}
        </div>
      </div>

      <div className="p-4">
        <div className="space-y-3">
          <div>
            <p className="font-medium text-gray-900">
              {getProductDisplayName(item.product)}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {item.approvedQuantity || item.quantity} {getProductDisplayUnit(item.product)}
            </p>
          </div>

          {/* ملاحظات الإكمال */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.taskNotes} <span className="text-gray-500">{t.optional}</span>
            </label>
            <textarea
              value={completionNotes[item.itemId] || ''}
              onChange={(e) => onUpdateNotes(item.itemId, e.target.value)}
              placeholder={isRtl ? 'أدخل ملاحظات الإكمال...' : 'Enter completion notes...'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={2}
            />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={() => onComplete(item.orderId, item.itemId, completionNotes[item.itemId])}
            disabled={actionLoading === `complete-${item.orderId}-${item.itemId}`}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50 transition-colors"
          >
            {actionLoading === `complete-${item.orderId}-${item.itemId}` ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            {t.complete}
          </button>
          
          <button
            onClick={() => onReject(item)}
            className="px-3 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm font-medium transition-colors"
          >
            {t.reject}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ========== Component: Completed Item Card ==========
const CompletedItemCard: React.FC<any> = ({ 
  item, 
  isRtl, 
  formatDate, 
  getStatusText, 
  getStatusColor, 
  getProductDisplayName, 
  getProductDisplayUnit, 
  t 
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white rounded-lg shadow-sm border p-3 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="font-medium text-gray-900 text-sm">
            {getProductDisplayName(item.product)}
          </p>
          <p className="text-xs text-gray-600">
            {item.approvedQuantity || item.quantity} {getProductDisplayUnit(item.product)}
          </p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
          {getStatusText(item.status)}
        </span>
      </div>
      
      <div className="text-xs text-gray-500">
        <div className="flex items-center gap-1 mb-1">
          <span>{item.orderNumber}</span>
          <span>•</span>
          <span>{formatDate(item.completedAt || item.orderCreatedAt)}</span>
        </div>
      </div>
    </motion.div>
  );
};

// ========== Component: Pending Approval Card ==========
const PendingApprovalCard: React.FC<any> = ({ 
  item, 
  isRtl, 
  formatDate, 
  getStatusText, 
  getStatusColor, 
  getProductDisplayName, 
  getProductDisplayUnit, 
  t 
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border overflow-hidden border-l-4 border-l-yellow-400"
    >
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="font-bold text-gray-900 text-sm">{item.orderNumber}</p>
            <p className="text-xs text-gray-500">{formatDate(item.orderCreatedAt)}</p>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
            {getStatusText(item.status)}
          </span>
        </div>
        
        <div className="mb-3">
          <p className="font-medium text-gray-900 text-sm">
            {getProductDisplayName(item.product)}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            {item.quantity} {getProductDisplayUnit(item.product)}
          </p>
        </div>
        
        <div className="bg-yellow-50 p-2 rounded">
          <p className="text-xs text-yellow-800 text-center">
            ⏳ {isRtl ? 'بانتظار موافقة الإدارة' : 'Awaiting admin approval'}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// ========== Component: Rejected Item Card ==========
const RejectedItemCard: React.FC<any> = ({ 
  item, 
  isRtl, 
  formatDate, 
  getStatusText, 
  getStatusColor, 
  getProductDisplayName, 
  getProductDisplayUnit, 
  t 
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border overflow-hidden border-l-4 border-l-red-400"
    >
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="font-bold text-gray-900 text-sm">{item.orderNumber}</p>
            <p className="text-xs text-gray-500">{formatDate(item.orderCreatedAt)}</p>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
            {getStatusText(item.status)}
          </span>
        </div>
        
        <div className="mb-3">
          <p className="font-medium text-gray-900 text-sm">
            {getProductDisplayName(item.product)}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            {item.quantity} {getProductDisplayUnit(item.product)}
          </p>
        </div>
        
        {item.rejectReason && (
          <div className="bg-red-50 p-2 rounded">
            <p className="text-xs font-medium text-red-800">{isRtl ? 'سبب الرفض:' : 'Rejection reason:'}</p>
            <p className="text-xs text-red-600 mt-1">{item.rejectReason}</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ========== Component: Orders Tab ==========
interface OrdersTabProps {
  orders: FactoryOrder[];
  isRtl: boolean;
  formatDate: (dateString: string) => string;
  getStatusText: (status: string) => string;
  getStatusColor: (status: string) => string;
  getPriorityText: (priority: string) => string;
  getPriorityColor: (priority: string) => string;
  getProductDisplayName: (product: any) => string;
  getProductDisplayUnit: (product: any) => string;
  t: any;
}

const OrdersTab: React.FC<OrdersTabProps> = ({ 
  orders, 
  isRtl, 
  formatDate, 
  getStatusText,
  getStatusColor,
  getPriorityText,
  getPriorityColor,
  getProductDisplayName,
  getProductDisplayUnit,
  t
}) => {
  if (orders.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-12 bg-white rounded-xl shadow-sm border"
      >
        <div className="p-4 bg-gray-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
          <ShoppingBag className="w-10 h-10 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t.myOrders} - {isRtl ? 'لا توجد طلبات' : 'No orders'}
        </h3>
        <p className="text-gray-600 max-w-md mx-auto mb-6">
          {isRtl
            ? 'لم تقم بإنشاء أي طلبات بعد. يمكنك إنشاء طلب جديد من الزر "طلب جديد" أعلاه.'
            : 'You have not created any orders yet. You can create a new order from the "New Order" button above.'
          }
        </p>
      </motion.div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {orders.map((order) => (
        <OrderCard 
          key={order._id}
          order={order}
          isRtl={isRtl}
          formatDate={formatDate}
          getStatusText={getStatusText}
          getStatusColor={getStatusColor}
          getPriorityText={getPriorityText}
          getPriorityColor={getPriorityColor}
          getProductDisplayName={getProductDisplayName}
          getProductDisplayUnit={getProductDisplayUnit}
          t={t}
        />
      ))}
    </div>
  );
};

// ========== Component: Order Card ==========
const OrderCard: React.FC<any> = ({
  order,
  isRtl,
  formatDate,
  getStatusText,
  getStatusColor,
  getPriorityText,
  getPriorityColor,
  getProductDisplayName,
  getProductDisplayUnit,
  t
}) => {
  const [expanded, setExpanded] = useState(false);

  const completedItems = order.items?.filter((item: any) => item.status === 'completed') || [];
  const progressPercentage = order.stats?.progressPercentage || 
    (order.items?.length > 0 ? Math.round((completedItems.length / order.items.length) * 100) : 0);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow"
    >
      <div className="p-4 border-b bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-gray-900">
              {t.orderNumber}: {order.orderNumber}
            </h3>
            <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(order.priority)}`}>
              {getPriorityText(order.priority)}
            </span>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
          >
            {expanded ? (
              <ChevronLeft className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-500" />
            )}
          </button>
        </div>
        
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {t.createdAt}: {formatDate(order.createdAt)}
          </p>
          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(order.status)}`}>
            {getStatusText(order.status)}
          </span>
        </div>
        
        {/* شريط التقدم */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
            <span>{isRtl ? 'التقدم' : 'Progress'}</span>
            <span>{progressPercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>
              {completedItems.length} {isRtl ? 'من' : 'of'} {order.items?.length || 0} {isRtl ? 'مكتمل' : 'completed'}
            </span>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="p-4 border-t">
          <div className="space-y-3">
            {order.items?.map((item: any) => (
              <div key={item._id} className="p-3 rounded-lg border border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 text-sm">
                      {getProductDisplayName(item.product)}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <p className="text-sm text-gray-600">
                        {item.quantity} {getProductDisplayUnit(item.product)}
                      </p>
                      {item.approvedQuantity && item.approvedQuantity !== item.quantity && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                          {isRtl ? 'معتمد:' : 'Approved:'} {item.approvedQuantity}
                        </span>
                      )}
                    </div>
                    
                    {item.assignedTo && (
                      <p className="text-xs text-gray-500 mt-1">
                        {t.assignedTo}: {isRtl ? item.assignedTo.name : (item.assignedTo.nameEn || item.assignedTo.name)}
                      </p>
                    )}
                    
                    {item.startedAt && (
                      <p className="text-xs text-gray-500 mt-1">
                        {t.started}: {formatDate(item.startedAt)}
                      </p>
                    )}
                    
                    {item.completedAt && (
                      <p className="text-xs text-green-500 mt-1">
                        {isRtl ? 'اكتمل في:' : 'Completed at:'} {formatDate(item.completedAt)}
                      </p>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                    {getStatusText(item.status)}
                  </span>
                </div>
                
                {item.notes && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">{t.notes}:</span> {item.notes}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {order.notes && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <span className="font-medium">{t.notes}:</span> {order.notes}
              </p>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

// ========== Component: Reject Modal ==========
interface RejectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  reason: string;
  onChangeReason: (reason: string) => void;
  loading: boolean;
  isRtl: boolean;
  t: any;
}

const RejectModal: React.FC<RejectModalProps> = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  reason, 
  onChangeReason,
  loading,
  isRtl,
  t
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-xl shadow-xl max-w-md w-full"
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                {t.rejectItem}
              </h3>
              <p className="text-sm text-gray-600">
                {isRtl ? 'الرجاء إدخال سبب الرفض' : 'Please enter rejection reason'}
              </p>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t.rejectionReason} *
            </label>
            <textarea
              value={reason}
              onChange={(e) => onChangeReason(e.target.value)}
              placeholder={isRtl ? 'أدخل سبب الرفض هنا...' : 'Enter rejection reason here...'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows={4}
            />
            <p className="text-xs text-gray-500 mt-1">
              {isRtl ? 'يجب أن يكون السبب 3 أحرف على الأقل' : 'Must be at least 3 characters'}
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              {t.cancel}
            </button>
            <button
              onClick={onConfirm}
              disabled={!reason.trim() || reason.trim().length < 3 || loading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isRtl ? 'جاري الرفض...' : 'Rejecting...'}
                </span>
              ) : (
                t.rejectItem
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ========== Component: Create Order Modal ==========
interface CreateOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  order: OrderFormState;
  products: Product[];
  onAddItem: () => void;
  onUpdateItem: (index: number, field: 'product' | 'quantity' | 'notes', value: string) => void;
  onRemoveItem: (index: number) => void;
  onOrderChange: (order: OrderFormState) => void;
  loading: boolean;
  isRtl: boolean;
  t: any;
  language: string;
  getProductDisplayName: (product: Product) => string;
}

const CreateOrderModal: React.FC<CreateOrderModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  order,
  products,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onOrderChange,
  loading,
  isRtl,
  t,
  language,
  getProductDisplayName
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Plus className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {t.createOrder}
                </h3>
                <p className="text-sm text-gray-600">
                  {isRtl ? 'أضف منتجات من أقسامك' : 'Add products from your departments'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XCircle className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* معلومات الطلب */}
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.orderNumber}
                </label>
                <input
                  type="text"
                  value={order.orderNumber}
                  onChange={(e) => onOrderChange({ ...order, orderNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder={isRtl ? 'رقم الطلب' : 'Order number'}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.priority}
                </label>
                <select
                  value={order.priority}
                  onChange={(e) => onOrderChange({ ...order, priority: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  <option value="low">{t.low}</option>
                  <option value="medium">{t.medium}</option>
                  <option value="high">{t.high}</option>
                  <option value="urgent">{t.urgent}</option>
                </select>
              </div>
            </div>
          </div>

          {/* العناصر */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-900">
                {t.products} ({order.items.length})
              </h4>
              <button
                onClick={onAddItem}
                className="flex items-center gap-1 px-3 py-2 text-sm bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors border border-amber-200"
              >
                <Plus className="w-4 h-4" />
                {t.addProduct}
              </button>
            </div>

            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">
                      {isRtl ? 'عنصر' : 'Item'} {index + 1}
                    </span>
                    {order.items.length > 1 && (
                      <button
                        onClick={() => onRemoveItem(index)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title={t.remove}
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t.selectProduct} *
                      </label>
                      <select
                        value={item.product}
                        onChange={(e) => onUpdateItem(index, 'product', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      >
                        <option value="">{isRtl ? '-- اختر المنتج --' : '-- Select Product --'}</option>
                        {products.map((product) => (
                          <option key={product._id} value={product._id}>
                            {getProductDisplayName(product)} - {product.code}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t.quantity} *
                      </label>
                      <div className="flex items-center">
                        <button
                          onClick={() => onUpdateItem(index, 'quantity', Math.max(1, item.quantity - 1).toString())}
                          className="px-3 py-2 border border-gray-300 rounded-l-lg hover:bg-gray-100 transition-colors"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => onUpdateItem(index, 'quantity', e.target.value)}
                          className="w-full px-3 py-2 border-t border-b border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-center text-sm"
                        />
                        <button
                          onClick={() => onUpdateItem(index, 'quantity', (item.quantity + 1).toString())}
                          className="px-3 py-2 border border-gray-300 rounded-r-lg hover:bg-gray-100 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t.notes} ({t.optional})
                      </label>
                      <input
                        type="text"
                        value={item.notes || ''}
                        onChange={(e) => onUpdateItem(index, 'notes', e.target.value)}
                        placeholder={isRtl ? 'ملاحظات خاصة بالعنصر' : 'Item-specific notes'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ملاحظات عامة */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t.notes} ({t.optional})
            </label>
            <textarea
              value={order.notes}
              onChange={(e) => onOrderChange({ ...order, notes: e.target.value })}
              placeholder={isRtl ? 'ملاحظات إضافية حول الطلب...' : 'Additional notes about the order...'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              rows={3}
            />
          </div>

          {/* الأزرار */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              {t.cancel}
            </button>
            <button
              onClick={onConfirm}
              disabled={loading || order.items.some(item => !item.product || item.quantity < 1)}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t.creating}
                </span>
              ) : (
                t.createOrder
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ChefOrdersDashboard;